import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/DWAuthContext';
import { MI_COLORS, MI_FONTS } from './miTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GATE_KEY = 'mi_beta_access';

export default function MirrorIndexGate() {
  const { user, loading: authLoading } = useAuth();
  const [granted, setGranted] = useState(() => localStorage.getItem(GATE_KEY) === 'granted');
  const [checking, setChecking] = useState(!granted);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // On mount, if the gate is disabled server-side, auto-open (future-proofs lifting the gate).
  useEffect(() => {
    if (granted) { setChecking(false); return; }
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/dw/beta-gate/status`);
        if (active && res.data && res.data.enabled === false) {
          localStorage.setItem(GATE_KEY, 'granted');
          setGranted(true);
        }
      } catch (e) {
        /* keep the gate up on error */
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [granted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/dw/beta-gate`, { password: password.trim() });
      localStorage.setItem(GATE_KEY, 'granted');
      setGranted(true);
    } catch (err) {
      setError('That access code is not right. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Logged-in users bypass the gate entirely.
  if (authLoading) {
    return (
      <div style={{ backgroundColor: MI_COLORS.ivory, minHeight: '100vh' }} className="flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: MI_COLORS.copper }} />
      </div>
    );
  }
  if (user || granted) return <Outlet />;

  if (checking) {
    return (
      <div style={{ backgroundColor: MI_COLORS.ivory, minHeight: '100vh' }} className="flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: MI_COLORS.copper }} />
      </div>
    );
  }

  return (
    <div
      data-testid="mi-gate"
      style={{ backgroundColor: MI_COLORS.ivory, fontFamily: MI_FONTS.body, color: MI_COLORS.graphite, minHeight: '100vh' }}
      className="flex items-center justify-center px-6"
    >
      <div
        style={{ backgroundColor: '#fff', border: `1px solid ${MI_COLORS.warmGrey}` }}
        className="w-full max-w-md rounded-3xl p-8 sm:p-10"
      >
        <div className="flex items-baseline gap-2 mb-8">
          <span style={{ fontFamily: MI_FONTS.display, fontWeight: 600, fontSize: '24px', color: MI_COLORS.indigoDeep, letterSpacing: '-0.01em' }}>
            Mirror Index
          </span>
          <span style={{ fontFamily: MI_FONTS.body, fontSize: '11px', color: MI_COLORS.graphiteSoft, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            by My Mirror Report
          </span>
        </div>

        <p style={{ fontFamily: MI_FONTS.body, fontSize: '12px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: MI_COLORS.copper, marginBottom: '14px' }}>
          Private beta
        </p>
        <h1 style={{ fontFamily: MI_FONTS.display, fontWeight: 500, fontSize: '30px', color: MI_COLORS.indigoDeep, letterSpacing: '-0.01em', lineHeight: 1.15, marginBottom: '12px' }}>
          Enter your access code
        </h1>
        <p style={{ fontFamily: MI_FONTS.body, fontSize: '14px', lineHeight: 1.6, color: MI_COLORS.graphiteSoft, marginBottom: '28px' }}>
          Mirror Index is in private beta. Enter the access code you were given to continue.
        </p>

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            data-testid="mi-gate-input"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            placeholder="Access code"
            autoFocus
            style={{
              width: '100%',
              border: `1px solid ${error ? MI_COLORS.terracotta : MI_COLORS.warmGrey}`,
              backgroundColor: MI_COLORS.ivory,
              fontFamily: MI_FONTS.body,
              fontSize: '15px',
              color: MI_COLORS.graphite,
            }}
            className="px-4 py-3 rounded-full outline-none mb-2"
          />
          {error && (
            <p data-testid="mi-gate-error" style={{ color: MI_COLORS.terracotta, fontFamily: MI_FONTS.body, fontSize: '13px', marginBottom: '10px' }}>
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="mi-gate-submit"
            disabled={submitting || !password.trim()}
            style={{
              backgroundColor: MI_COLORS.indigoDeep,
              color: '#fff',
              fontFamily: MI_FONTS.body,
              fontSize: '14px',
              fontWeight: 500,
              opacity: submitting || !password.trim() ? 0.6 : 1,
            }}
            className="w-full px-5 py-3 rounded-full mt-3 hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
}
